import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import {
  Callout,
  Field,
  IconCheck,
  IconCheckCircle,
  PasswordInput,
  PublicActions,
  PublicLink,
  PublicPage,
  PublicPrimary,
  PublicSecondary,
  PublicShell,
  PublicTitle,
  Spinner,
} from '../../ui/v2';
import { AuthRequestError, resetPassword, validateResetLink } from './authApi';
import {
  PASSWORD_CHANGED_NOTICE,
  PASSWORD_RULES,
  SIGN_IN_ERROR,
  passwordMeetsRules,
} from './authModel';

type Screen = 'checking' | 'invalid' | 'form' | 'done';

/**
 * Reset password — `/reset-password?uid=…&token=…`.
 *
 * Four calm screens:
 *   checking   the link is validated before anything else is shown
 *   invalid    one generic screen — expired, used and malformed look the
 *              same, because the API doesn't say which — with one action
 *   form       new password with a live rules list, confirm, one primary
 *   done       "Password updated" and one action: sign in (with a notice in
 *              router state). No auto-redirect; the user moves when ready.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const token = params.get('token');

  const [screen, setScreen] = useState<Screen>('checking');
  const [checkFailed, setCheckFailed] = useState(false);
  const [checkError, setCheckError] = useState<
    typeof SIGN_IN_ERROR.network | typeof SIGN_IN_ERROR.server
  >(SIGN_IN_ERROR.network);
  const [attempt, setAttempt] = useState(0);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touchedConfirm, setTouchedConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setScreen('checking');
    setCheckFailed(false);
    validateResetLink(uid, token)
      .then((valid) => {
        if (!cancelled) setScreen(valid ? 'form' : 'invalid');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCheckFailed(true);
        setCheckError(
          err instanceof AuthRequestError && err.kind === 'server'
            ? SIGN_IN_ERROR.server
            : SIGN_IN_ERROR.network,
        );
        setScreen('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [uid, token, attempt]);

  const mismatch = touchedConfirm && confirm.length > 0 && confirm !== password;
  const rulesOk = passwordMeetsRules(password);
  const reason = !password
    ? 'Choose a password'
    : !rulesOk
      ? 'Meet both rules'
      : !confirm
        ? 'Type it again to confirm'
        : confirm !== password
          ? 'The two passwords differ'
          : undefined;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setTouchedConfirm(true);
    if (reason) {
      (!password || !rulesOk ? passwordRef : confirmRef).current?.focus();
      return;
    }
    setBusy(true);
    setServerError(null);
    try {
      await resetPassword(uid ?? '', token ?? '', password);
      setScreen('done');
    } catch (err) {
      setServerError(
        err instanceof AuthRequestError && err.kind === 'validation'
          ? err.message
          : err instanceof AuthRequestError && err.kind === 'server'
            ? SIGN_IN_ERROR.server
            : SIGN_IN_ERROR.network,
      );
      passwordRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicPage>
      <PageMeta
        title="Set a new password · AskArthur"
        description="Set a new AskArthur password"
      />
      <PublicShell width="form">
        {screen === 'checking' && (
          <>
            <PublicTitle>Checking your link…</PublicTitle>
            <div
              className="mt-6 flex items-center gap-3 text-v2-13 text-v2-fg-2"
              role="status"
              aria-live="polite"
            >
              <Spinner className="h-4 w-4" /> One moment
            </div>
          </>
        )}

        {screen === 'invalid' && (
          <>
            <PublicTitle
              lede={
                checkFailed
                  ? 'We couldn’t check this link. Try again in a moment, or request a new one.'
                  : 'Reset links work once and stop working after a while. Request a new one and use the most recent email.'
              }
            >
              {checkFailed
                ? 'Couldn’t check this link'
                : 'This link isn’t valid'}
            </PublicTitle>
            {checkFailed && (
              <Callout tone="danger" className="mt-5">
                {checkError}
              </Callout>
            )}
            <PublicActions align="between" className="mt-6">
              {checkFailed ? (
                <PublicSecondary onClick={() => setAttempt((a) => a + 1)}>
                  Try again
                </PublicSecondary>
              ) : (
                <PublicSecondary onClick={() => navigate('/signin')}>
                  Back to sign in
                </PublicSecondary>
              )}
              <PublicPrimary onClick={() => navigate('/forgot-password')}>
                Request a new link
              </PublicPrimary>
            </PublicActions>
          </>
        )}

        {screen === 'form' && (
          <>
            <PublicTitle lede="Pick something you don’t use anywhere else. You’ll sign in with it right after.">
              Choose a new password
            </PublicTitle>
            <form
              onSubmit={submit}
              noValidate
              className="mt-6 flex flex-col gap-4"
            >
              <Field label="New password">
                {(id, describedBy) => (
                  <>
                    <PasswordInput
                      ref={passwordRef}
                      id={id}
                      autoComplete="new-password"
                      autoFocus
                      aria-describedby={`${id}-rules${describedBy ? ` ${describedBy}` : ''}`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="[&>input]:h-10 [&>input]:text-v2-14"
                    />
                    <ul
                      id={`${id}-rules`}
                      className="mt-1.5 flex flex-col gap-0.5 text-v2-12"
                      aria-label="Password rules"
                    >
                      {PASSWORD_RULES.map((r) => {
                        const ok = r.test(password);
                        return (
                          <li
                            key={r.key}
                            className={clsx(
                              'flex items-center gap-1.5',
                              ok ? 'text-v2-fg-2' : 'text-v2-fg-3',
                            )}
                          >
                            <span
                              className={clsx(
                                'inline-flex h-3.5 w-3.5 items-center justify-center rounded-full',
                                ok
                                  ? 'bg-v2-success/15 text-v2-success'
                                  : 'border border-v2-line-strong',
                              )}
                              aria-hidden
                            >
                              {ok && <IconCheck size={9} />}
                            </span>
                            {r.label}
                            <span className="sr-only">
                              {ok ? ' — met' : ' — not yet'}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </Field>
              <Field
                label="Confirm new password"
                error={mismatch ? 'The two passwords differ.' : undefined}
              >
                {(id, describedBy) => (
                  <PasswordInput
                    ref={confirmRef}
                    id={id}
                    autoComplete="new-password"
                    aria-describedby={describedBy}
                    invalid={mismatch}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onBlur={() => setTouchedConfirm(true)}
                    className="[&>input]:h-10 [&>input]:text-v2-14"
                  />
                )}
              </Field>
              {serverError && <Callout tone="danger">{serverError}</Callout>}
              <PublicPrimary
                type="submit"
                loading={busy}
                disabledReason={touchedConfirm ? reason : undefined}
                className="mt-1 sm:w-full"
              >
                Set new password
              </PublicPrimary>
              {touchedConfirm && reason && !mismatch && (
                <p className="-mt-2 text-v2-12 text-v2-fg-3">{reason}.</p>
              )}
            </form>
            <p className="mt-6 text-v2-12">
              <PublicLink to="/signin" className="text-v2-12">
                ← Back to sign in
              </PublicLink>
            </p>
          </>
        )}

        {screen === 'done' && (
          <>
            <div className="flex items-start gap-3">
              <span className="mt-1 text-v2-success" aria-hidden>
                <IconCheckCircle size={22} />
              </span>
              <PublicTitle lede="Use it the next time you sign in. If you didn’t make this change, tell us straight away.">
                Password updated
              </PublicTitle>
            </div>
            <PublicActions className="mt-6">
              <PublicPrimary
                onClick={() =>
                  navigate('/signin', {
                    state: { message: PASSWORD_CHANGED_NOTICE },
                  })
                }
              >
                Sign in
              </PublicPrimary>
            </PublicActions>
          </>
        )}
      </PublicShell>
    </PublicPage>
  );
}
